import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EventService from "../../services/EventService";
import AddToCalendarButton from "./AddToCalendarButton";
import EventShell, { EventError, EventLoading } from "./EventShell";
import {
  audienceLabel,
  capacityLabel,
  externalUrl,
  formatEventRange,
  formatLabel,
  googleCalendarUrl,
  timezoneNote,
  typeLabel,
  venueMapUrl,
} from "./eventFormat";

/**
 * Event detail at /events/:slug.
 *
 * The server already returned this page's <head> (title, Open Graph, JSON-LD)
 * via the meta-shell, so this component only renders the body.
 */
const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      const result = await EventService.getEvent(slug);
      if (!active) return;
      if (result.success) {
        setEvent(result.event);
      } else {
        setError(result.message || "We couldn't find that event.");
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <EventShell>
        <EventLoading label="Loading event…" />
      </EventShell>
    );
  }

  if (error || !event) {
    return (
      <EventShell>
        <EventError message={error || "Event not found."} />
        <Link className="ev-btn ev-btn-secondary" to="/events">
          Back to all events
        </Link>
      </EventShell>
    );
  }

  const cancelled = event.status === "CANCELLED";
  const zoneNote = timezoneNote(event.timezone, event.startsAt);
  const registerLabel = event.full
    ? event.waitlistEnabled
      ? "Join the waitlist"
      : "This event is full"
    : "Register — free";
  const canRegister =
    event.registrationOpen &&
    !cancelled &&
    (!event.full || event.waitlistEnabled);
  const mapHref = venueMapUrl(event.venue);

  return (
    <EventShell
      crumbs={[{ label: "Events", to: "/events" }, { label: event.title }]}
    >
      {event.heroImageUrl && (
        <img className="ev-hero-image" src={event.heroImageUrl} alt="" />
      )}

      <div className="ev-badges">
        <span className="ev-badge">{formatLabel(event.format)}</span>
        <span className="ev-badge">{typeLabel(event.eventType)}</span>
        {event.audience && (
          <span className="ev-badge">{audienceLabel(event.audience)}</span>
        )}
        {event.full && (
          <span className="ev-badge ev-badge-warn">
            {event.waitlistEnabled ? "Waitlist open" : "Full"}
          </span>
        )}
      </div>

      <h1 className="ev-h1">{event.title}</h1>
      {event.summary && <p className="ev-lead">{event.summary}</p>}

      {cancelled && (
        <div className="ev-alert ev-alert-error" role="alert">
          <strong>This event has been cancelled.</strong>
          {event.cancellationReason && <> {event.cancellationReason}</>}
          <div className="ev-hint" style={{ marginTop: 8 }}>
            Registered attendees have been notified by email. You don't need to
            do anything — please remove it from your calendar.
          </div>
        </div>
      )}

      <div className="ev-panel ev-panel-accent">
        <div className="ev-facts">
          <div className="ev-fact">
            <span className="ev-fact-label">When</span>
            <span>
              {formatEventRange(event.startsAt, event.endsAt)}
              {zoneNote && <div className="ev-hint">{zoneNote}</div>}
            </span>
          </div>

          <div className="ev-fact">
            <span className="ev-fact-label">Where</span>
            <span>
              {event.format === "VIRTUAL" ? (
                <>
                  Online
                  {event.virtual?.platform
                    ? ` — ${event.virtual.platform}`
                    : ""}
                  <div className="ev-hint">
                    A join link is sent to registrants shortly before it starts.
                  </div>
                </>
              ) : (
                <>
                  <strong>
                    {event.venue?.name || "Venue to be announced"}
                  </strong>
                  {event.venue?.address && <div>{event.venue.address}</div>}
                  {mapHref && (
                    <a
                      className="ev-header-link"
                      href={mapHref}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Open in maps
                    </a>
                  )}
                </>
              )}
            </span>
          </div>

          <div className="ev-fact">
            <span className="ev-fact-label">Cost</span>
            <span>Free</span>
          </div>

          {capacityLabel(event) && (
            <div className="ev-fact">
              <span className="ev-fact-label">Availability</span>
              <span>{capacityLabel(event)}</span>
            </div>
          )}
        </div>

        {/* Accessibility details are a booking factor for an older audience,
            not a footnote — surfaced next to the venue, never buried. */}
        {event.venue?.accessibilityNotes && (
          <div
            className="ev-alert ev-alert-info"
            style={{ marginTop: 18, marginBottom: 0 }}
          >
            <strong>Accessibility: </strong>
            {event.venue.accessibilityNotes}
          </div>
        )}
      </div>

      <div className="ev-btn-row">
        {canRegister ? (
          <Link
            className="ev-btn ev-btn-primary"
            to={`/events/${event.slug}/register`}
          >
            {registerLabel}
          </Link>
        ) : (
          <button className="ev-btn ev-btn-primary" disabled>
            {cancelled ? "Event cancelled" : registerLabel}
          </button>
        )}
        {!cancelled && (
          <>
            <AddToCalendarButton slug={event.slug} />
            <a
              className="ev-btn ev-btn-quiet"
              href={googleCalendarUrl(event)}
              target="_blank"
              rel="noreferrer noopener"
            >
              Google Calendar
            </a>
          </>
        )}
      </div>

      {event.descriptionHtml && (
        <>
          <h2 className="ev-h2">About this session</h2>
          {/* Authored by platform admins only and sanitized server-side. */}
          <div dangerouslySetInnerHTML={{ __html: event.descriptionHtml }} />
        </>
      )}

      {Array.isArray(event.agenda) && event.agenda.length > 0 && (
        <>
          <h2 className="ev-h2">What we'll cover</h2>
          {event.agenda.map((item, index) => (
            <div className="ev-agenda-item" key={index}>
              {item.time && <div className="ev-agenda-time">{item.time}</div>}
              <div>
                <strong>{item.title}</strong>
                {item.description && (
                  <div className="ev-muted">{item.description}</div>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {Array.isArray(event.speakers) && event.speakers.length > 0 && (
        <>
          <h2 className="ev-h2">Who's presenting</h2>
          {event.speakers.map((speaker, index) => (
            <div className="ev-speaker" key={index}>
              {speaker.photoUrl && <img src={speaker.photoUrl} alt="" />}
              <div>
                <strong>{speaker.name}</strong>
                {speaker.title && (
                  <div className="ev-muted ev-small">{speaker.title}</div>
                )}
                {speaker.bio && (
                  <p style={{ margin: "8px 0 0" }}>{speaker.bio}</p>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {Array.isArray(event.faq) && event.faq.length > 0 && (
        <>
          <h2 className="ev-h2">Common questions</h2>
          {event.faq.map((item, index) => (
            <div key={index}>
              <p className="ev-faq-q">{item.question}</p>
              <p className="ev-muted" style={{ marginTop: 0 }}>
                {item.answer}
              </p>
            </div>
          ))}
        </>
      )}

      {Array.isArray(event.resources) && event.resources.length > 0 && (
        <>
          <h2 className="ev-h2">Resources</h2>
          <ul>
            {event.resources.map((resource) => (
              <li key={resource.id}>
                <a
                  className="ev-header-link"
                  href={externalUrl(resource.url)}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {resource.title}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="ev-panel" style={{ marginTop: 40 }}>
        <h2 className="ev-h2" style={{ marginTop: 0, border: "none" }}>
          Can't wait until the session?
        </h2>
        <p className="ev-muted">
          Paste a suspicious text, email, or link into KinShield right now and
          see what it finds. No account needed.
        </p>
        <a
          className="ev-btn ev-btn-secondary"
          href="/?version=standard#tryitout"
        >
          Check something suspicious
        </a>
      </div>

      {canRegister && (
        <div className="ev-sticky-cta">
          <Link
            className="ev-btn ev-btn-primary"
            to={`/events/${event.slug}/register`}
          >
            {registerLabel}
          </Link>
        </div>
      )}
    </EventShell>
  );
};

export default EventDetail;
